/**
 * PaymentModal
 * 
 * Modal for recording payments with details:
 * - Amount (partial or full)
 * - Payment Method (Cash, Check, Zelle, Credit Card, Other)
 * - Received By (free text)
 * - Payment Date
 * - Notes
 * 
 * Also displays payment history for the order.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Trash2, DollarSign, Calendar, User, CreditCard, FileText, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'other', label: 'Other' },
];

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString();
  } catch {
    return dateStr;
  }
}

function formatCurrency(amount) {
  return `$${Math.floor(amount || 0)}`;
}

function PaymentModal({ 
  open, 
  onClose, 
  order, 
  onPaymentRecorded,
  currentUser,
  viewMode = false  // When true, only show payment history (for paid orders)
}) {
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Form state
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [receivedBy, setReceivedBy] = useState('');
  // Use local date to avoid timezone issues (ISO string in UTC can be tomorrow)
  const today = new Date();
  const [paymentDate, setPaymentDate] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  );
  const [notes, setNotes] = useState('');
  
  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  // Calculate order balance - use local state that updates after payment
  const orderTotal = order?.total_amount || 0;
  const orderAdvance = order?.advance_payment || 0;
  const orderDiscount = order?.discount_amount || 0;
  
  // NOTE: total_amount already has discount applied (it's the final total after discount)
  // So balance = total_amount - advance_payment - payments_received
  // We show discount separately in the UI for clarity, but don't subtract it from balance calculation
  const computedBalance = orderTotal - orderAdvance - totalPaid;
  const currentBalance = Math.max(0, computedBalance);  // Don't show negative
  const isPaid = currentBalance <= 0.01;  // Allow for floating point tolerance

  // Load payment history when modal opens
  useEffect(() => {
    if (open && order?.id) {
      loadPayments();
      // Reset form fields - don't auto-fill receivedBy to ensure proper accountability
      setReceivedBy('');
    }
  }, [open, order?.id]);

  // Update amount field when balance changes
  useEffect(() => {
    if (!isPaid) {
      setAmount(Math.floor(currentBalance).toString());
    }
  }, [currentBalance, isPaid]);

  const loadPayments = async () => {
    try {
      const resp = await axios.get(`${API}/orders/${order.id}/payments`);
      setPayments(resp.data?.payments || []);
      setTotalPaid(resp.data?.total_paid || 0);
    } catch (err) {
      console.error('Failed to load payments:', err);
    }
  };

  const resetForm = () => {
    setAmount(Math.floor(currentBalance).toString());
    setPaymentMethod('cash');
    setReceivedBy(''); // Always blank - staff must enter manually
    // Use local date to avoid timezone issues
    const today = new Date();
    setPaymentDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
    setNotes('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    // Allow slight overpayment for rounding (up to $1 or 1% of balance)
    const maxAllowed = currentBalance + Math.max(1.0, currentBalance * 0.01);
    if (amountNum > maxAllowed) {
      toast.error(`Amount significantly exceeds remaining balance of ${formatCurrency(currentBalance)}`);
      return;
    }
    
    if (!receivedBy.trim()) {
      toast.error('Please enter who received the payment');
      return;
    }
    
    if (!paymentDate) {
      toast.error('Please select a payment date');
      return;
    }

    setLoading(true);
    try {
      const resp = await axios.post(`${API}/orders/${order.id}/record-payment`, {
        amount: amountNum,
        payment_method: paymentMethod,
        received_by: receivedBy.trim(),
        payment_date: paymentDate,
        notes: notes.trim()
      });
      
      toast.success(`Payment of ${formatCurrency(amountNum)} recorded successfully`);
      
      // Reload payments to update totalPaid and recalculate balance
      await loadPayments();
      
      // Reset form and hide it
      resetForm();
      setShowAddForm(false);
      
      // Notify parent to refresh order
      if (onPaymentRecorded) {
        onPaymentRecorded(resp.data);
      }
      
      // Auto-close modal if fully paid
      if (resp.data?.is_fully_paid) {
        toast.success('Order is now fully paid!');
        setTimeout(() => onClose(), 800);
      }
      
    } catch (err) {
      console.error('Failed to record payment:', err);
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    setPaymentToDelete(paymentId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeletePayment = async () => {
    if (!paymentToDelete) return;
    
    try {
      await axios.delete(`${API}/orders/${order.id}/payments/${paymentToDelete}`);
      toast.success('Payment deleted');
      await loadPayments();
      if (onPaymentRecorded) {
        onPaymentRecorded();
      }
    } catch (err) {
      console.error('Failed to delete payment:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete payment');
    } finally {
      setDeleteConfirmOpen(false);
      setPaymentToDelete(null);
    }
  };

  const handlePayFull = () => {
    setAmount(Math.floor(currentBalance).toString());
    setShowAddForm(true);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            {viewMode ? 'Payment Details' : 'Record Payment'}
          </DialogTitle>
        </DialogHeader>

        {/* Customer Info for paid orders */}
        {viewMode && order?.customer_info && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-green-600">PAID IN FULL</Badge>
            </div>
            <p className="text-sm font-medium text-gray-800">{order.customer_info.name}</p>
            <p className="text-xs text-gray-600">{order.customer_info.phone}</p>
            {order.customer_info.occasion && (
              <p className="text-xs text-gray-500 mt-1">{order.customer_info.occasion}</p>
            )}
          </div>
        )}

        {/* Order Summary */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Order Total</span>
            <span className="font-semibold">{formatCurrency(orderTotal)}</span>
          </div>
          {orderDiscount > 0 && (
            <div className="flex justify-between items-center mb-2 text-sm text-gray-500">
              <span className="italic">(Includes {formatCurrency(orderDiscount)} discount)</span>
              <span></span>
            </div>
          )}
          {orderAdvance > 0 && (
            <div className="flex justify-between items-center mb-2 text-sm">
              <span className="text-gray-600">Advance Paid</span>
              <span className="text-green-600">-{formatCurrency(orderAdvance)}</span>
            </div>
          )}
          {totalPaid > 0 && (
            <div className="flex justify-between items-center mb-2 text-sm">
              <span className="text-gray-600">Payments Received</span>
              <span className="text-green-600">-{formatCurrency(totalPaid)}</span>
            </div>
          )}
          <div className="border-t pt-2 flex justify-between items-center">
            <span className="font-medium">Balance Due</span>
            <span className={`text-lg font-bold ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
              {isPaid ? 'PAID' : formatCurrency(currentBalance)}
            </span>
          </div>
        </div>

        {/* Payment History */}
        {payments.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Payment History ({payments.length})
            </h4>
            <div className={`space-y-3 ${viewMode ? 'max-h-60' : 'max-h-40'} overflow-y-auto`}>
              {payments.map((p) => (
                <div 
                  key={p.id} 
                  className={`bg-white border rounded-lg p-4 text-sm ${viewMode ? 'border-green-200 shadow-sm' : ''}`}
                  data-testid={`payment-record-${p.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Amount and Method - Top Row */}
                      <div className="flex items-center gap-3 mb-3">
                        <span className="font-bold text-green-600 text-xl">{formatCurrency(p.amount)}</span>
                        <Badge variant="outline" className={`text-xs ${viewMode ? 'border-green-400 text-green-700' : ''}`}>
                          {PAYMENT_METHODS.find(m => m.value === p.payment_method)?.label || p.payment_method}
                        </Badge>
                      </div>
                      
                      {/* Payment Details Grid */}
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                          <User className="w-4 h-4 text-blue-500" />
                          <div>
                            <span className="text-xs text-gray-500 block">Received By</span>
                            <span className="font-semibold text-gray-800">{p.received_by || 'Not specified'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                          <Calendar className="w-4 h-4 text-orange-500" />
                          <div>
                            <span className="text-xs text-gray-500 block">Payment Date</span>
                            <span className="font-semibold text-gray-800">{formatDate(p.payment_date)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Notes if present */}
                      {p.notes && (
                        <div className="mt-2 text-xs text-gray-600 bg-yellow-50 p-2 rounded border border-yellow-200">
                          <strong>Note:</strong> {p.notes}
                        </div>
                      )}
                    </div>
                    
                    {/* Delete button - only in edit mode */}
                    {!viewMode && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 ml-2"
                        onClick={() => handleDeletePayment(p.id)}
                        data-testid={`delete-payment-${p.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No payments yet message for viewMode */}
        {viewMode && payments.length === 0 && isPaid && (
          <div className="text-center py-4 text-gray-500">
            <p className="text-sm">No payment records found.</p>
            <p className="text-xs mt-1">This order may have been paid via advance payment.</p>
          </div>
        )}

        {/* Add Payment Form - only show when NOT in viewMode and order is not paid */}
        {!viewMode && !isPaid && (
          <>
            {!showAddForm ? (
              <div className="flex gap-2">
                <Button 
                  onClick={handlePayFull} 
                  className="flex-1"
                  data-testid="pay-full-btn"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Pay Full Balance ({formatCurrency(currentBalance)})
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowAddForm(true)}
                  data-testid="add-partial-payment-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Partial
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {/* Amount */}
                  <div>
                    <Label htmlFor="amount" className="text-sm flex items-center gap-1">
                      <DollarSign className="w-3 h-3" /> Amount
                    </Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      data-testid="payment-amount-input"
                      required
                    />
                  </div>

                  {/* Payment Method */}
                  <div>
                    <Label htmlFor="method" className="text-sm flex items-center gap-1">
                      <CreditCard className="w-3 h-3" /> Method
                    </Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger data-testid="payment-method-select">
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Received By */}
                  <div>
                    <Label htmlFor="receivedBy" className="text-sm flex items-center gap-1">
                      <User className="w-3 h-3" /> Received By
                    </Label>
                    <Input
                      id="receivedBy"
                      type="text"
                      value={receivedBy}
                      onChange={(e) => setReceivedBy(e.target.value)}
                      placeholder="Name"
                      data-testid="payment-received-by-input"
                      required
                    />
                  </div>

                  {/* Payment Date */}
                  <div>
                    <Label htmlFor="paymentDate" className="text-sm flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Date
                    </Label>
                    <Input
                      id="paymentDate"
                      type="date"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(e.target.value)}
                      data-testid="payment-date-input"
                      required
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes" className="text-sm">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Check #, reference, or any notes..."
                    rows={2}
                    data-testid="payment-notes-input"
                  />
                </div>

                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowAddForm(false);
                      resetForm();
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="flex-1"
                    data-testid="submit-payment-btn"
                  >
                    {loading ? 'Recording...' : `Record ${formatCurrency(parseFloat(amount) || 0)}`}
                  </Button>
                </div>
              </form>
            )}
          </>
        )}

        {isPaid && (
          <div className="text-center py-4">
            <Badge className="bg-green-100 text-green-800 text-lg px-4 py-2">
              Fully Paid
            </Badge>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="close-payment-modal-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this payment record? The balance will be restored to the order.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPaymentToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeletePayment}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

export default PaymentModal;
